import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  reportsApi,
  adminReportsApi,
  type PeriodStats,
  type SummaryResponse,
  type RunListItem,
  type RunListResponse,
  type RunDetailResponse,
  type ProcessedFileItem,
  type ProcessedFileListResponse,
} from "../api/reports";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";

// ─── Status badge ────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const s = status.toUpperCase();
  let cls = "bg-gray-100 text-gray-700 dark:bg-slate-600 dark:text-slate-300";
  if (s === "DONE" || s === "OK")
    cls = "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400";
  else if (s === "FAILED" || s.startsWith("FAILED_"))
    cls = "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400";
  else if (s === "RUNNING")
    cls = "bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400";
  else if (s === "NO_MATCH")
    cls = "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400";
  else if (s === "STOPPED")
    cls = "bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-400";

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

// ─── Stat card ───────────────────────────────────────
function StatCard({ label, value, color = "text-slate-900 dark:text-white" }: {
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <div className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
      <h3 className={`text-2xl font-bold mt-1 ${color}`}>{value}</h3>
    </div>
  );
}

// ─── Period stats row ────────────────────────────────
function PeriodRow({ label, stats }: { label: string; stats: PeriodStats }) {
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition">
      <td className="px-6 py-3 font-medium text-slate-900 dark:text-white">{label}</td>
      <td className="px-6 py-3 text-right">{stats.runs}</td>
      <td className="px-6 py-3 text-right">{stats.files_processed}</td>
      <td className="px-6 py-3 text-right text-green-600">{stats.files_ok}</td>
      <td className="px-6 py-3 text-right text-red-600">{stats.files_failed}</td>
      <td className="px-6 py-3 text-right">{stats.pages_billed.toLocaleString()}</td>
      <td className="px-6 py-3 text-right">{stats.tokens_consumed.toLocaleString()}</td>
      <td className="px-6 py-3 text-right">
        {(stats.failure_rate * 100).toFixed(1)}%
      </td>
    </tr>
  );
}

// ─── Format datetime ─────────────────────────────────
function fmtDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ─── Pagination controls ─────────────────────────────
function Pagination({ page, totalPages, onPage }: {
  page: number;
  totalPages: number;
  onPage: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200 dark:border-slate-700">
      <span className="text-sm text-slate-500 dark:text-slate-400">
        Page {page} of {totalPages}
      </span>
      <div className="flex gap-2">
        <button
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════
// Main Reports Page
// ═════════════════════════════════════════════════════

const ReportsPage: React.FC = () => {
  const { role } = useAuth();
  const isSuperAdmin = role === "SUPERADMIN";

  // --- state ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [runsData, setRunsData] = useState<RunListResponse | null>(null);
  const [runsPage, setRunsPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [jobTypeFilter, setJobTypeFilter] = useState("");

  // Drill-down state
  const [selectedRun, setSelectedRun] = useState<RunDetailResponse | null>(null);
  const [runFiles, setRunFiles] = useState<ProcessedFileListResponse | null>(null);
  const [filesPage, setFilesPage] = useState(1);
  const [drillLoading, setDrillLoading] = useState(false);

  // --- fetch summary + runs ---
  const fetchData = async (page = 1) => {
    try {
      setError(null);
      setLoading(true);

      const params: any = { page, per_page: 15 };
      if (statusFilter) params.status = statusFilter;
      if (jobTypeFilter) params.job_type = jobTypeFilter;

      const [summaryRes, runsRes] = await Promise.all([
        isSuperAdmin ? adminReportsApi.summary() : reportsApi.summary(),
        isSuperAdmin ? adminReportsApi.runs(params) : reportsApi.runs(params),
      ]);

      setSummary(summaryRes);
      setRunsData(runsRes);
      setRunsPage(page);
    } catch (err: any) {
      setError(err.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, [statusFilter, jobTypeFilter]);

  // --- drill into run ---
  const openRunDetail = async (runId: string) => {
    try {
      setDrillLoading(true);
      const [detail, files] = await Promise.all([
        reportsApi.runDetail(runId),
        reportsApi.runFiles(runId, { page: 1, per_page: 20 }),
      ]);
      setSelectedRun(detail);
      setRunFiles(files);
      setFilesPage(1);
    } catch (err: any) {
      setError(err.message || "Failed to load run details");
    } finally {
      setDrillLoading(false);
    }
  };

  const fetchRunFiles = async (runId: string, page: number) => {
    try {
      const files = await reportsApi.runFiles(runId, { page, per_page: 20 });
      setRunFiles(files);
      setFilesPage(page);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ─── Run Detail View ───
  if (selectedRun) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-8 font-sans">
        <div className="max-w-7xl mx-auto">
          {/* Back button */}
          <button
            onClick={() => { setSelectedRun(null); setRunFiles(null); }}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-500 mb-6"
          >
            <ArrowLeft size={16} /> Back to Runs
          </button>

          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Run Detail
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 font-mono">
            {selectedRun.id}
          </p>

          {/* Run info cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Status" value={selectedRun.status} />
            <StatCard label="Job Type" value={selectedRun.job_type || "-"} />
            <StatCard label="Total Files" value={selectedRun.files_total} />
            <StatCard label="Pages Billed" value={selectedRun.pages_billed} />
          </div>

          {/* File breakdown */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center gap-3">
              <CheckCircle className="text-green-500" size={20} />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">OK</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">{selectedRun.file_status_breakdown.ok}</p>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center gap-3">
              <AlertTriangle className="text-yellow-500" size={20} />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">No Match</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">{selectedRun.file_status_breakdown.no_match}</p>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center gap-3">
              <XCircle className="text-red-500" size={20} />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Failed</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">{selectedRun.file_status_breakdown.failed}</p>
              </div>
            </div>
          </div>

          {/* Run metadata */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 mb-8">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Run Info</h2>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-slate-500 dark:text-slate-400">Machine ID</span>
              <span className="text-slate-900 dark:text-white font-mono text-xs">{selectedRun.machine_id || "-"}</span>
              <span className="text-slate-500 dark:text-slate-400">Input Path</span>
              <span className="text-slate-900 dark:text-white font-mono text-xs truncate">{selectedRun.input_path || "-"}</span>
              <span className="text-slate-500 dark:text-slate-400">Output Root</span>
              <span className="text-slate-900 dark:text-white font-mono text-xs truncate">{selectedRun.output_root || "-"}</span>
              <span className="text-slate-500 dark:text-slate-400">Edge Version</span>
              <span className="text-slate-900 dark:text-white">{selectedRun.edge_version || "-"}</span>
              <span className="text-slate-500 dark:text-slate-400">Started</span>
              <span className="text-slate-900 dark:text-white">{fmtDate(selectedRun.started_at)}</span>
              <span className="text-slate-500 dark:text-slate-400">Ended</span>
              <span className="text-slate-900 dark:text-white">{fmtDate(selectedRun.ended_at)}</span>
            </div>
          </div>

          {/* Processed files table */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Processed Files
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
                <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-900 dark:text-white font-medium">
                  <tr>
                    <th className="px-6 py-3">Filename</th>
                    <th className="px-6 py-3">Job Type</th>
                    <th className="px-6 py-3 text-right">Pages</th>
                    <th className="px-6 py-3 text-right">Billed</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Failure Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {runFiles?.files.map((f) => (
                    <tr key={f.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition">
                      <td className="px-6 py-3 font-medium text-slate-900 dark:text-white max-w-xs truncate">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-slate-400 flex-shrink-0" />
                          {f.source_filename}
                        </div>
                      </td>
                      <td className="px-6 py-3">{f.job_type}</td>
                      <td className="px-6 py-3 text-right">{f.original_pages}</td>
                      <td className="px-6 py-3 text-right">{f.pages_billed}</td>
                      <td className="px-6 py-3"><StatusBadge status={f.status} /></td>
                      <td className="px-6 py-3 text-xs text-slate-500 max-w-xs truncate">{f.failure_reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(!runFiles?.files || runFiles.files.length === 0) && (
                <div className="p-8 text-center text-slate-400">No files found for this run.</div>
              )}
            </div>

            {runFiles && (
              <Pagination
                page={filesPage}
                totalPages={runFiles.pagination.total_pages}
                onPage={(p) => fetchRunFiles(selectedRun.id, p)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Main List View ────
  if (loading) {
    return (
      <div className="p-8 text-slate-900 dark:text-white">Loading reports...</div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl p-6 text-red-700 dark:text-red-400">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            {isSuperAdmin ? "System Reports" : "Reports"}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            {isSuperAdmin ? "Cross-organization overview" : "Edge processing activity"}
          </p>
        </div>

        {/* Summary stats cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Runs (24h)" value={summary.last_24h.runs} color="text-blue-600" />
            <StatCard label="Files (24h)" value={summary.last_24h.files_processed} />
            <StatCard label="Pages Billed (24h)" value={summary.last_24h.pages_billed.toLocaleString()} />
            <StatCard
              label="Failure Rate (24h)"
              value={`${(summary.last_24h.failure_rate * 100).toFixed(1)}%`}
              color={summary.last_24h.failure_rate > 0.1 ? "text-red-600" : "text-green-600"}
            />
          </div>
        )}

        {/* Period breakdown table */}
        {summary && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden mb-8">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Summary by Period
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
                <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-900 dark:text-white font-medium">
                  <tr>
                    <th className="px-6 py-3">Period</th>
                    <th className="px-6 py-3 text-right">Runs</th>
                    <th className="px-6 py-3 text-right">Files</th>
                    <th className="px-6 py-3 text-right">OK</th>
                    <th className="px-6 py-3 text-right">Failed</th>
                    <th className="px-6 py-3 text-right">Pages</th>
                    <th className="px-6 py-3 text-right">Tokens</th>
                    <th className="px-6 py-3 text-right">Failure %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  <PeriodRow label="Last 24h" stats={summary.last_24h} />
                  <PeriodRow label="Last 7 days" stats={summary.last_7d} />
                  <PeriodRow label="Last 30 days" stats={summary.last_30d} />
                  <PeriodRow label="All Time" stats={summary.all_time} />
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Runs table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Job Runs
            </h2>

            {/* Filters */}
            <div className="flex gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="">All Statuses</option>
                <option value="DONE">Done</option>
                <option value="RUNNING">Running</option>
                <option value="FAILED">Failed</option>
                <option value="STOPPED">Stopped</option>
              </select>

              <select
                value={jobTypeFilter}
                onChange={(e) => setJobTypeFilter(e.target.value)}
                className="text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="">All Job Types</option>
                <option value="REDACT">Redact</option>
                <option value="SPLIT">Split</option>
                <option value="REDACT_SPLIT">Redact + Split</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
              <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-900 dark:text-white font-medium">
                <tr>
                  {isSuperAdmin && <th className="px-6 py-3">Org ID</th>}
                  <th className="px-6 py-3">Job Type</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Files</th>
                  <th className="px-6 py-3 text-right">OK</th>
                  <th className="px-6 py-3 text-right">Failed</th>
                  <th className="px-6 py-3 text-right">Pages</th>
                  <th className="px-6 py-3">Started</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {runsData?.runs.map((run) => (
                  <tr key={run.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition">
                    {isSuperAdmin && (
                      <td className="px-6 py-3 font-mono text-xs">{run.org_id || "-"}</td>
                    )}
                    <td className="px-6 py-3">{run.job_type || "-"}</td>
                    <td className="px-6 py-3"><StatusBadge status={run.status} /></td>
                    <td className="px-6 py-3 text-right">{run.files_total}</td>
                    <td className="px-6 py-3 text-right text-green-600">{run.files_ok}</td>
                    <td className="px-6 py-3 text-right text-red-600">{run.files_failed}</td>
                    <td className="px-6 py-3 text-right">{run.pages_billed}</td>
                    <td className="px-6 py-3 whitespace-nowrap">{fmtDate(run.started_at)}</td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => openRunDetail(run.id)}
                        className="text-blue-600 hover:text-blue-500 font-medium text-sm"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {(!runsData?.runs || runsData.runs.length === 0) && (
              <div className="p-8 text-center text-slate-400">
                No job runs found.
              </div>
            )}
          </div>

          {runsData && (
            <Pagination
              page={runsPage}
              totalPages={runsData.pagination.total_pages}
              onPage={(p) => fetchData(p)}
            />
          )}
        </div>

        {drillLoading && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-xl">
              <p className="text-slate-900 dark:text-white">Loading run details...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
